import React, { useCallback } from 'react';
import cx from 'classnames';
import { Button, Menu, MenuItem } from '@material-ui/core';

import { PAGES, useTrackingEvent } from 'src/services';
import * as events from 'src/lib/events';
import { STONLY_ID } from 'src/lib/stonly';
import PlusIcon from 'src/assets/icons/Plus';
import { BlogWizardIcon, EditorIcon } from 'src/assets/icons/navbar-links';
import usePopoverAnchor from '../../../../utils/hooks/usePopoverAnchor';
import { ITEM_TYPE } from '../../const';
import { SidebarSubmenu } from '../../components/SidebarSubmenu';
import { SidebarLink } from '../../components/SidebarLink';
import {
  useActiveLocationPath,
  useAutoExpandActiveMenuItem,
  useNavigationSidebarItems,
} from '../../service';
import { useViewState } from './useViewState';
import { useHandlers } from './useHandlers';
import { useStyles } from './useStyles';
import { useSidebarBrandVoices } from 'src/services/useSidebarBrandVoices';

const MENU_PROPS = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'right',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'right',
  },
  getContentAnchorEl: null,
};

export const SidebarItems = ({ className }) => {
  const styles = useStyles();
  const { menuItemId, collapseItemId } = useViewState();
  const brandVoices = useSidebarBrandVoices();
  const activePath = useActiveLocationPath();
  const [anchorEl, openMenu, closeMenu] = usePopoverAnchor();
  const open = !!anchorEl;
  const items = useNavigationSidebarItems(brandVoices);
  useAutoExpandActiveMenuItem(items, activePath);
  const trackingEvent = useTrackingEvent();

  const {
    handleToggleMenu,
    handleToggleCollapse,
    handleLinkClick,
    handleOpenGenerationToolPopup,
    handleOpenBlogWizardClick,
  } = useHandlers({ items });

  const handleProjectCreationMenuOpen = useCallback(
    (event) => {
      openMenu(event.currentTarget);
      trackingEvent(events.DASHBOARD.clickedCreateProjectMenuButton, {
        page: PAGES.homePage,
      });
    },
    [openMenu, trackingEvent]
  );

  const handleProjectCreationMenuClose = useCallback(() => {
    closeMenu(null);
  }, [closeMenu]);

  const handleCreateDataDrivenProject = useCallback(() => {
    trackingEvent(events.DASHBOARD.clickedCreateDataDrivenButton, {
      page: PAGES.homePage,
    });
    handleOpenGenerationToolPopup(true);
    handleProjectCreationMenuClose();
  }, [handleProjectCreationMenuClose, handleOpenGenerationToolPopup, trackingEvent]);

  const handleCreateBlogProject = useCallback(() => {
    trackingEvent(events.DASHBOARD.clickedCreateBlogButton, {
      page: PAGES.homePage,
    });
    handleOpenBlogWizardClick();
    handleProjectCreationMenuClose();
  }, [handleProjectCreationMenuClose, handleOpenBlogWizardClick, trackingEvent]);

  return (
    <div className={cx(styles.root, className)}>
      <Button
        className={styles.createProjectButton}
        classes={{
          startIcon: styles.createProjectButtonStartIcon,
        }}
        onClick={handleProjectCreationMenuOpen}
        startIcon={<PlusIcon />}
        variant="text"
        color="default"
        data-stonly-id={STONLY_ID.createDocumentButton}
      >
        Create
      </Button>
      <Menu
        classes={{
          paper: styles.menu_paper,
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleProjectCreationMenuClose}
        variant="menu"
        keepMounted
        {...MENU_PROPS}
      >
        <MenuItem
          className={styles.menuItem}
          onClick={handleCreateDataDrivenProject}
        >
          <EditorIcon />
          Data-driven Editor
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          onClick={handleCreateBlogProject}
        >
          <BlogWizardIcon />
          Blog wizard
        </MenuItem>
      </Menu>

      <div className={styles.divider}></div>

      <div className={styles.menuItemsWrapper}>
        {items.map((item) => {
          if (item.disabled) {
            return null;
          }

          if (item.type === ITEM_TYPE.spacer) {
            return (
              <div
                className={styles.spacer}
                key={item.id}
              />
            );
          }

          if (item.type === ITEM_TYPE.menu) {
            return (
              <SidebarSubmenu
                className={cx(styles.item, styles.submenu)}
                key={item.id}
                item={item}
                menuItemId={menuItemId}
                collapseItemId={collapseItemId}
                onToggleCollapse={handleToggleCollapse}
                onToggleMenu={handleToggleMenu}
                activePath={activePath}
                dataStonlyId={item.dataStonlyId}
              />
            );
          }

          return (
            <SidebarLink
              className={cx(styles.item, styles.link)}
              key={item.id}
              item={item}
              active={item.path === activePath}
              onClick={handleLinkClick}
              style={item.style}
              dataStonlyId={item.dataStonlyId}
            />
          );
        })}
      </div>
    </div>
  );
};
